import React, { useEffect, useState } from "react";
import { Theme } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import PetsIcon from "@mui/icons-material/Pets";
import PetCard, { PetCardPet } from "./PetCard";
import { useSnackbar } from "notistack";
import { useRepositories } from "../repositories";
import { PetRecord, VetRecord } from "../repositories/types";

interface PetContainerProps {
  pets: PetRecord[];
  clientID: string;
  vets: VetRecord[];
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    titleBlock: {
      display: "flex",
      "justify-content": "space-between",
    },
    petContainer: {
      display: "flex",
      "flex-wrap": "wrap",
    },
    titleIcon: {
      display: "flex",
    },
  })
);

const PetContainer: React.FC<PetContainerProps> = (props: PetContainerProps) => {
  const classes = useStyles();
  const [allPets, setAllPets] = useState<PetCardPet[]>([]);
  const { enqueueSnackbar } = useSnackbar();
  const { pets: petRepo } = useRepositories();

  useEffect(() => {
    setAllPets(
      props.pets.map((p) => ({ ...p, isNew: false, editable: false }))
    );
  }, [props.pets]);

  const addPet = () => {
    const pet: PetCardPet = {
      id: uuidv4(),
      clientID: props.clientID,
      Name: "",
      Type: "Dog",
      Neutered: true,
      Breed: "",
      VaccinationDue: "",
      Notes: "",
      OffLead: false,
      TermsAccepted: false,
      editable: true,
      isNew: true,
    };
    setAllPets(allPets.concat(pet));
  };

  const savePet = async (pet: PetCardPet) => {
    try {
      const petID = pet.id;
      const savedPet = await petRepo.save(pet, pet.isNew);
      const pets: PetCardPet[] = Object.assign([], allPets);
      const index = allPets.findIndex((p) => p.id === petID);
      const newPet: PetCardPet = { ...pet, ...savedPet, editable: false, isNew: false };
      pets[index] = newPet;
      setAllPets(pets);
    } catch (e) {
      enqueueSnackbar("Failed to save pet", { variant: "error" });
    }
  };

  const deletePet = async (id: string) => {
    try {
      await petRepo.delete(id);
      setAllPets(allPets.filter((p) => p.id !== id));
    } catch (e) {
      enqueueSnackbar("Failed to delete pet", { variant: "error" });
    }
  };

  const uuidv4 = () => {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
      //eslint-disable-next-line
      var r = (Math.random() * 16) | 0,
        v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };

  return (
    <Paper variant="outlined" square>
      <div className={classes.titleBlock}>
        <div className={classes.titleIcon}>
          <PetsIcon />
          <Typography variant="h6">Pets</Typography>
        </div>
        <Button id="add-pet" aria-label="Add Pet" variant="contained" color="secondary" onClick={addPet}>
          Add Pet
        </Button>
      </div>
      <div className={classes.petContainer}>
        {allPets.map((pet) => (
          <PetCard key={pet.id} pet={pet} save={savePet} delete={deletePet} vets={props.vets} />
        ))}
      </div>
    </Paper>
  );
};

export default PetContainer;
